import React from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  DataPagination,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
  Text,
  cn,
} from '@musa.tech/design-system';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskGeneratorName } from '@/utils/demoMode';
import type { BucketContract } from '@/integrations/agreements/client';
import { establishmentName, formatAddress } from '@/utils/onDemandRequestRules';
import { describeBucketAdvance, describeBucketStayLimits, describeBucketUsage } from '@/utils/bucketRequestRules';
import { EmptyMessage, EstablishmentFilter } from './ContractSelectionStep';

export const BUCKET_CONTRACTS_PAGE_SIZE = 20;

export interface BucketContractSelectionStepProps {
  contracts: BucketContract[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  isError: boolean;
  generatorId: string;
  onGeneratorChange: (generatorId: string) => void;
  requiresGenerator: boolean;
  selectedContractId: number | null;
  onSelect: (contract: BucketContract) => void;
  headerSlot?: React.ReactNode;
}

/**
 * Escolha do contrato de movimentação de equipamento. Acima do limite de simultâneas o contrato
 * continua selecionável: o aviso só informa, como no agendamento pelo Hub.
 */
export const BucketContractSelectionStep: React.FC<BucketContractSelectionStepProps> = ({
  contracts,
  total,
  page,
  onPageChange,
  isLoading,
  isError,
  generatorId,
  onGeneratorChange,
  requiresGenerator,
  selectedContractId,
  onSelect,
  headerSlot,
}) => {
  const { isDemoMode } = useDemoMode();

  return (
    <div className="flex flex-col gap-4">
      {headerSlot}
      <div className="flex flex-col gap-1">
        <Text as="h2" variant="heading" size="small" className="text-neutral-900">
          Escolha o contrato de movimentação de equipamento
        </Text>
        <Text variant="body" size="small" className="text-neutral-500">
          Endereço e acondicionamento vêm do contrato. O transportador aceita ou recusa o pedido.
        </Text>
      </div>

      <EstablishmentFilter
        generatorId={generatorId}
        onGeneratorChange={onGeneratorChange}
        requiresGenerator={requiresGenerator}
      />

      {requiresGenerator && !generatorId ? (
        <EmptyMessage
          title="Selecione um estabelecimento"
          description="Escolha o estabelecimento para ver os contratos de movimentação de equipamento dele."
        />
      ) : isLoading ? (
        <div className="flex flex-col gap-2" data-testid="bucket-contracts-loading">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertTitle>Não foi possível carregar os contratos de movimentação de equipamento</AlertTitle>
          <AlertDescription>
            O serviço de coletas está indisponível no momento. Tente novamente em alguns minutos.
          </AlertDescription>
        </Alert>
      ) : !contracts.length ? (
        <EmptyMessage
          title="Nenhum contrato de movimentação de equipamento ativo"
          description="Sem contrato de movimentação de equipamento vigente não dá para pedir colocação, retirada ou troca. Fale com a Musa para contratar o serviço."
        />
      ) : (
        <RadioGroup
          value={selectedContractId != null ? String(selectedContractId) : ''}
          onValueChange={(value) => {
            const contract = contracts.find((c) => String(c.contractId) === value);
            if (contract) onSelect(contract);
          }}
          className="block overflow-x-auto rounded-lg border border-border bg-white"
        >
          <DataTable>
            <DataTableHeader>
              <DataTableRow>
                <DataTableHead className="w-10" />
                <DataTableHead>Estabelecimento</DataTableHead>
                <DataTableHead>Acondicionamento</DataTableHead>
                <DataTableHead>No endereço</DataTableHead>
                <DataTableHead>Regra</DataTableHead>
              </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
              {contracts.map((contract) => {
                const selected = contract.contractId === selectedContractId;

                return (
                  <DataTableRow
                    key={contract.contractId}
                    data-testid={`bucket-contract-row-${contract.contractId}`}
                    aria-selected={selected}
                    onClick={() => onSelect(contract)}
                    className={cn('cursor-pointer', selected && 'bg-primary-50')}
                  >
                    <DataTableCell>
                      <RadioGroupItem value={String(contract.contractId)} aria-label={`Contrato ${contract.contractId}`} />
                    </DataTableCell>
                    <DataTableCell>
                      <Text variant="body" size="small" className="font-medium text-neutral-900">
                        {maskGeneratorName(establishmentName(contract.generator), isDemoMode)}
                      </Text>
                      <Text variant="body" size="small" className="text-neutral-500">
                        {formatAddress(contract.address)}
                      </Text>
                    </DataTableCell>
                    <DataTableCell>
                      <Text variant="body" size="small" className="text-neutral-900">
                        {contract.packaging.name ?? '—'}
                      </Text>
                      <Text variant="body" size="small" className="text-neutral-500">
                        {contract.material.name ?? '—'}
                      </Text>
                    </DataTableCell>
                    <DataTableCell>
                      <div className="flex flex-col items-start gap-1">
                        <Text variant="body" size="small" className="text-neutral-700">
                          {describeBucketUsage(contract)}
                        </Text>
                        {contract.aboveLimit && <Badge variant="warning" className="whitespace-nowrap">Acima do limite</Badge>}
                      </div>
                    </DataTableCell>
                    <DataTableCell>
                      <Text variant="body" size="small" className="text-neutral-700">
                        {describeBucketAdvance(contract.rule)}
                      </Text>
                      <Text variant="body" size="small" className="text-neutral-500">
                        {describeBucketStayLimits(contract)}
                      </Text>
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
          </DataTable>
        </RadioGroup>
      )}

      {total > BUCKET_CONTRACTS_PAGE_SIZE && (
        <DataPagination page={page} pageSize={BUCKET_CONTRACTS_PAGE_SIZE} total={total} onChange={onPageChange} />
      )}
    </div>
  );
};
